import * as React from "react";
import {Component } from "react";

import {observer} from "mobx-react";
import {Item, Table, List} from "semantic-ui-react";
import {NavigationConfiguration} from "../../../config/navigation-configuration";
import {Icon} from "../../../core/components/icon/icon";
import {Grundstueck} from "../../../models/grundstueck";
import {Eingang} from "../../../models/gebaeude";
import {Projekt} from "../../../models/projekt";
import {autorun, runInAction} from "mobx";
import {ContentInfoBox} from "./content-info-box";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import SVG from "react-inlinesvg";
import {Koordinaten} from "./koordinaten";
import {ProjektTable, ProjektDataItem} from "./tables/projekt-table";
import {MapViewerStore} from "../../../stores/map-viewer-store-mobx";
import {withStore} from "../../../core/stores/abstract-app-store";

export interface ObjectResultProjektProps {
    store?: MapViewerStore; // this is injected by withStore
    projekt: Projekt | null;
    prepareKontakt: (grundstueck: Grundstueck | undefined) => void;
    handleErrorAndNotFound: () => void;
    handleHighlight: (projekt: Projekt, geometry: __esri.Geometry | undefined) => void;
    handleHighlightClick: (e: React.MouseEvent | undefined,
        item: Eingang | __esri.Point | null, layerNo?: number) => void;
    handleShowGrundstueck: (egrid: string, e: React.MouseEvent) => void;
    handleShowProjekt: (bage: string, e: React.MouseEvent) => void;
}

@observer
class ObjectResultProjektBase extends Component<ObjectResultProjektProps, any> {
    watchHandles: any[] = [];
    constructor(props: ObjectResultProjektProps) {
        super(props);
    }

    componentDidMount(): void {
        const { handleHighlight, prepareKontakt, projekt, store } = this.props;
        autorun((reaction: any) => {
            if (!store || !store.mapStore.view) {
                return;
            }
            if (!projekt) {
                return;
            }
            reaction.dispose();
            store.oStore.loadProjektPreview();
            prepareKontakt(projekt.grundstuecke? projekt.grundstuecke[0]: undefined);
            let geometry;
            const geometries: __esri.Geometry[] = [];
            if (projekt.grundstuecke) {
                projekt.grundstuecke.forEach((grundstueck: Grundstueck) => {
                    geometries.push(grundstueck.geometry);
                });
            }
            if (geometries.length > 1) {
                geometry = geometryEngine.union(geometries);
            } else if (geometries.length === 1) {
                geometry = geometries[0];
            }
            handleHighlight(projekt, geometry);
            const breadcrumbs = new NavigationConfiguration();
            const newRoute = "/bauprojekt/" + projekt.bagenr;
            breadcrumbs.items = [
                { title: "Bauprojekt" },
                { href: newRoute, title: projekt.bagenr }
            ];
            runInAction(()=> {
                store.appStore.setPageTitle("Bauprojekt Nr. " + projekt.bagenr
                    + " in " + projekt.attributes.GEMEINDE
                    + " (" + projekt.attributes.GBPER_NAME + ")");
                store.appStore.setTopContent(null);
                store.appStore.setBottomContent(null);
                store.appStore.setBreadcrumbs(breadcrumbs);
                store.appStore.setRoutePath(newRoute);
                store.appStore.setObjectId(projekt.bagenr);
                store.appStore.setTopicById("bauprojekt");
            });
        });
    }

    componentWillUnmount(): void {
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
    }
    handleHighlightClick(e: React.MouseEvent | undefined,
                         item: Eingang | __esri.Point | null,
                         layerNo?: number | undefined): void {
        this.props.handleHighlightClick(e, item, layerNo);
    }
    render(): React.ReactElement | null {
        const { projekt, store } = this.props;
        if (!store || !store.mapStore.view) {
            return null;
        }
        if (!projekt) {
            // this.errorAndNotFound("grundstueck");
            return null;
        }
        const { appConfig, mapViewerConfig, oStore } = store;
        let infoBlock: React.ReactElement | null = null;
        const projektBlock: React.ReactElement[] = [];

        const przList: React.ReactElement[] = [];
        const projektData: Map<string, ProjektDataItem[]> = new Map();
        if (projekt.grundstuecke) {
            const currentList: number[] = [];
            projekt.grundstuecke.forEach((grundstueck: Grundstueck) => {
                currentList.push(grundstueck.nummer);
                przList.push(
                    <List.Item key={grundstueck.egrid}>
                        <a href={appConfig.basename + "grundstueck/" + grundstueck.egrid}
                            onClick={(event: React.MouseEvent) =>
                                this.props.handleShowGrundstueck(grundstueck.egrid, event)}>
                            <span className={"linked"}>{grundstueck.nummer}</span></a>
                    </List.Item>
                );
                const pData: ProjektDataItem[] = [];
                grundstueck.projekte?.forEach((p) => {
                    if (p.bagenr !== projekt.bagenr){
                        pData.push(p);
                    }
                });
                projektData.set(grundstueck.egrid, pData);
            });
            // get list of not existing Grundstueck Nummern
            const missingNummer = projekt.prznummern.filter((n: number) => currentList.indexOf(n) === -1);
            missingNummer.forEach((nummer) => {
                przList.push(
                    <List.Item key={nummer} className={"error"}>
                        {nummer} <Icon name={"sign_warning"} selected />
                    </List.Item>
                );
            });
        }
        const statusSymbol = oStore.getProjektPreview(projekt.status);
        const description = (<Item.Content><Item.Header as={"h3"}>Projektinformationen</Item.Header>
            <Item.Description>
                <Table basic="very" compact>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell>Amtliche Baudossier-Nr.</Table.Cell>
                            <Table.Cell>{projekt.bagenr}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Gemeinde</Table.Cell>
                            <Table.Cell>{projekt.attributes.GEMEINDE}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Grundbuch (GB-Nr.)</Table.Cell>
                            <Table.Cell>{projekt.attributes.GBPER_NAME} ({projekt.attributes.GBPER_CODE})
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Grundstück-Nr</Table.Cell>
                            <Table.Cell>
                                <List divided horizontal className={"grundstuecke"}>{przList}</List>
                            </Table.Cell>
                        </Table.Row>
                        <Koordinaten
                            mapViewerConfig={mapViewerConfig}
                            coordinates={projekt.geometry}
                            handleHighlightClick={(e: React.MouseEvent | undefined, item: __esri.Point | null,
                                                   layerNo: number) => this.handleHighlightClick(e, item, layerNo)}
                            highlightable={true} selected={true} title={"Standort Bauprojekt"}/>
                        <Table.Row>
                            <Table.Cell>Verfahrenstyp</Table.Cell>
                            <Table.Cell>{projekt.attributes.VERFHRTYP}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Dossiertyp</Table.Cell>
                            <Table.Cell>{projekt.attributes.DOSSTYP}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Umschreibung Bauprojekt</Table.Cell>
                            <Table.Cell>{projekt.gegenstand}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Status</Table.Cell>
                            <Table.Cell>
                                <div className={"geo-status"}>
                                    <div className={"esri-symbol"}>
                                        {statusSymbol? (<SVG src={statusSymbol} />): ""}
                                    </div><div className={"geo-status-name"}>{projekt.status}</div>
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table></Item.Description></Item.Content>);
        const infoItems: React.ReactElement[] = [];
        let ebageUrl = null;
        const ebageAction = appConfig.predefinedActions.find((action) => action.id === "ebage");
        if (ebageAction && ebageAction.url) {
            ebageUrl = ebageAction.url + "" + projekt.bagenr;
        }
        infoItems.push(
            <ContentInfoBox
                mapViewerConfig={mapViewerConfig}
                key={"ebage"}
                type={"ebage"}
                url={ebageUrl}
            />
        );
        projektData.forEach((pData: ProjektDataItem[], index: string) => {
            const title = "Weitere Bauprojekte auf dem Grundstück " + index;
            projektBlock.push (
                <ProjektTable
                    data={pData}
                    key={index + "-" + oStore.selectedObjektId + "-" + pData.length}
                    handleShowProjekt={(bagenr: string, event: React.MouseEvent)=>
                        this.props.handleShowProjekt(bagenr, event)}
                    title={title}/>
            );
        });
        if (infoItems.length > 0) {
            infoBlock = (<Item.Group className={"geo-more-info"} divided>{infoItems}</Item.Group>);
        }
        return (<div>
            <Item.Group>
                <Item>{description}</Item>
            </Item.Group>
            {projektBlock}
            {infoBlock}
        </div>);
    }
}

export const ObjectResultProjekt = withStore(ObjectResultProjektBase);
